import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { cutoffsOfMonth, monthRange, pickCutoffSource } from '@tahi/core';
import { Button, ScreenHeader } from '../../../ui';
import { Segmented } from '../../../ui/Segmented';
import { CashFlowView } from '../components/CashFlowView';
import { copy } from '../copy';
import { cashFlowOf, monthWindow } from '../derive';
import { monthLabel, monthOf, rangeShort, shiftMonth } from '../format';
import { useBudgetSnapshot, useToday } from '../hooks';
import { SCREEN } from '../styles';

const BACK = { to: '/m/budget', label: copy.moduleName };
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export function CashFlowScreen() {
  const [sp, setSp] = useSearchParams();
  const today = useToday();
  const param = sp.get('month') ?? '';
  const month = MONTH_RE.test(param) ? param : monthOf(today);
  const view = sp.get('view') ?? 'month';
  const range = useMemo(() => monthWindow(month), [month]);
  const s = useBudgetSnapshot(range);

  if (!s) {
    return (
      <main className={SCREEN}>
        <ScreenHeader title={copy.cashFlow} back={BACK} />
        <p className="text-muted">{copy.loading}</p>
      </main>
    );
  }

  const cutoffs = cutoffsOfMonth(month, pickCutoffSource(s.sources));
  const cutoff = cutoffs.find((c) => `c${c.index}` === view);
  const period = cutoff ? cutoff.range : monthRange(month);
  const cf = cashFlowOf(s, period, cutoff ? 'cutoff' : 'month');
  const goMonth = (m: string) => setSp({ month: m });

  return (
    <main className={SCREEN}>
      <ScreenHeader title={copy.cashFlow} back={BACK} />
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="secondary"
          aria-label={copy.prevMonth}
          onClick={() => goMonth(shiftMonth(month, -1))}
        >
          ‹
        </Button>
        <h2 className="text-[20px] font-bold" aria-live="polite">
          {monthLabel(month)}
        </h2>
        <Button
          variant="secondary"
          aria-label={copy.nextMonth}
          onClick={() => goMonth(shiftMonth(month, 1))}
        >
          ›
        </Button>
      </div>
      <Segmented
        label={copy.period}
        value={cutoff ? view : 'month'}
        options={[
          { value: 'month', label: copy.monthOption },
          ...cutoffs.map((c) => ({ value: `c${c.index}`, label: copy.cutoffOption(c.index) })),
        ]}
        onChange={(v) => setSp({ month, view: v })}
      />
      <p className="text-sm text-muted">{rangeShort(period)}</p>
      <CashFlowView cf={cf} entries={s.entries} />
    </main>
  );
}
